/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Primitive } from 'c3';
import { ChartAxisData, ConfusionMatrixYAxis } from 'src/core/types/chart';

/**
 * Transforms the given data to suit the format of the line chart data which is:
 * [[xAxisLabel, ...xData], [yAxisLabel, ...yData]]
 *
 * @param xData The x axis data.
 * @param yData The y axis data.
 */
export const transformLineChartData = <X extends Primitive>(
    xData: ChartAxisData<X>,
    yData: ChartAxisData<number>
): [string, ...(X | number)[]][] =>
    isZeroedLineChartData(yData)
        ? []
        : [
              [xData.label, ...xData.data],
              [yData.label, ...yData.data]
          ];

/**
 * Transforms the given data to suit the format of the donut chart data which is:
 * [xAxisValue, yAxisValue][]
 *
 * @param xData The x axis data.
 * @param yData The y axis data.
 */
export const transformDonutChartData = <X extends Primitive>(xData: ChartAxisData<X>, yData: ChartAxisData<number>): [X, number][] =>
    xData.data.map((item, index) => [item, yData.data[index]]);
/**
 * Transforms the given data to suit the format of the stacked bar chart data which is:
 * [groupName, ...yData[groupIndex][]
 *
 * @param groups The groups that separates the y data.
 * @param yData The y axis data.
 */
export const transformStackedBarChartData = (groups: string[], yData: ChartAxisData<number[]>): [string, ...number[]][] => {
    const ret: [string, ...number[]][] = groups.map((groupName, groupIndex) =>
        yData.data[groupIndex] && yData.data[groupIndex].length > 0 ? [groupName, ...yData.data[groupIndex]] : ['noGroup']
    );
    const totalLength: number = ret.reduce((acc, value) => acc + (value.length > 1 ? 0 : 1), 0);

    return totalLength === ret.length ? [] : ret;
};

/**
 * Transforms the given data to suit the format of the confusion matrix chart data which is:
 * [[xAxisLabel, ...xData], [yAxisLabel, ...yData]].
 *
 * @param xData The x axis data.
 * @param yData The y axis data.
 */
export const transformConfusionMatrixChartData = (
    xData: ChartAxisData<number>,
    yData: ChartAxisData<ConfusionMatrixYAxis>
): [string, ...Primitive[]][] => [
    [xData.label, ...xData.data],
    [yData.label, ...yData.data.map(d => d.score)]
];

/**
 * Checks if the total sum of the y axis data is zero for line chart.
 *
 * @param data The data to be checked.
 */
const isZeroedLineChartData = (data: ChartAxisData<number>): boolean => data.data.reduce((acc, val) => acc + val, 0) === 0;
